import BreadCrumbs from 'components/breadcrumbs'
import CustomButton from 'components/Buttons/CustomButton'
import PMBPagination from 'components/PMBPagination'
import { TableComponent as DataTable } from 'components/TableComponent'
import debounce from 'lodash.debounce'
import moment from 'moment-timezone'
import { useMemo, useState } from 'react'
import { AlertCircle, ArrowLeft, ChevronDown, ChevronRight, Search, Users } from 'react-feather'
import { useSearchParams } from 'react-router-dom'
import { Card, CardBody, FormGroup, Input, InputGroupText, Spinner } from 'reactstrap'
import TabManageStaffGroup from '../components/TabManageStaffGroup'
import StaffList from '../staffList'
import { useFetchSharedGroupsQuery } from './sharedFarmersApiSlice'

const SharedFarmers = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const resourceIdFromUrl = searchParams.get('resourceId')
  const [sourcesPage, setSourcesPage] = useState(0)
  const [sourcesSize, setSourcesSize] = useState(10)
  const [sourcesSearch, setSourcesSearch] = useState('')
  const [debouncedSourcesSearch, setDebouncedSourcesSearch] = useState('')
  const debouncedSourceSearchHandler = useMemo(
    () => debounce((value) => { setDebouncedSourcesSearch(value || ''); setSourcesPage(0) }, 400),
    [],
  )
  const handleSourceSearchChange = (event) => {
    const value = event.target.value
    setSourcesSearch(value)
    debouncedSourceSearchHandler(value)
  }
  const { data: sharedGroupsData, isLoading: isLoadingSharedGroups, isFetching: isFetchingSharedGroups } =
    useFetchSharedGroupsQuery({
      page: resourceIdFromUrl ? 0 : sourcesPage,
      size: resourceIdFromUrl ? 100 : sourcesSize,
      search: resourceIdFromUrl ? undefined : debouncedSourcesSearch || undefined,
    })
  const sharedGroups = sharedGroupsData?.items || []
  const totalSharedGroups = sharedGroupsData?.totalItems ?? sharedGroups.length
  const selectedSharedGroup = resourceIdFromUrl
    ? sharedGroups.find((group) => String(group.resourceId) === String(resourceIdFromUrl))
    : null
  const handleSelectSource = (group) => {
    if (group?.resourceId) setSearchParams({ resourceId: group.resourceId })
  }
  const handleBackToSources = () => setSearchParams({})

  const sourceColumns = useMemo(
    () => [
      {
        name: <span>Sumber Grup / Pemilik</span>,
        selector: 'ownerName',
        grow: 3,
        sortable: true,
        cell: (row) => (
          <div className="d-flex align-items-center gap-3 py-2">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: '40px',
                height: '40px',
                minWidth: '40px',
                backgroundColor: '#E0F2FE',
                color: '#005697',
                fontWeight: 600,
                fontSize: '0.9rem',
              }}
            >
              {row?.ownerName
                ? row.ownerName.trim().substring(0, 2).toUpperCase()
                : 'SG'}
            </div>
            <div className="d-flex flex-column">
              <span className="fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>
                {row?.ownerName || `Sumber #${row?.resourceId}`}
              </span>
            </div>
          </div>
        ),
      },
      {
        name: <span>Peran / Akses</span>,
        selector: 'role',
        grow: 2,
        sortable: true,
        cell: (row) => (
          <span
            className="badge rounded-pill font-semibold"
            style={{
              backgroundColor: '#EFF8FF',
              color: '#175CD3',
              padding: '6px 14px',
              fontSize: '0.8rem',
            }}
          >
            {row?.role || 'VIEWER'}
          </span>
        ),
      },
      {
        name: <span>Tanggal Dibagikan</span>,
        selector: 'createdAt',
        grow: 2,
        sortable: true,
        cell: (row) => (
          <span className="text-muted" style={{ fontSize: '0.875rem' }}>
            {row?.createdAt
              ? moment(row.createdAt).locale('id').format('DD MMM YYYY, HH:mm')
              : '-'}
          </span>
        ),
      },
      {
        name: <span className="pe-2">Aksi</span>,
        width: '180px',
        right: true,
        cell: (row) => (
          <div className="d-flex align-items-center justify-content-end w-100">
            <CustomButton
              color="primary"
              outline
              size="sm"
              onClick={(e) => {
                e?.stopPropagation?.()
                handleSelectSource(row)
              }}
              testId={`select-shared-group-${row?.resourceId}`}
              className="d-inline-flex align-items-center gap-1 text-nowrap rounded-pill px-3 py-1 font-semibold"
              style={{ fontSize: '0.825rem' }}
            >
              <span>Lihat Anggota</span>
              <ChevronRight size={15} />
            </CustomButton>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const renderSourceMobileCard = (row, index) => {
    return (
      <Card
        key={row?.resourceId || index}
        className="mb-3 border-0 shadow-sm"
        style={{ borderRadius: '12px', overflow: 'hidden' }}
        data-testid={`shared-source-mobile-card-${row?.resourceId || index}`}
      >
        <CardBody className="p-0">
          <div
            className="p-3 d-flex align-items-center justify-content-between"
            style={{
              backgroundColor: '#F9FAFB',
              borderBottom: '1px solid #F2F4F7',
            }}
          >
            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
              <div
                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: '#E0F2FE',
                  color: '#005697',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                }}
              >
                {row?.ownerName
                  ? row.ownerName.trim().substring(0, 2).toUpperCase()
                  : 'SG'}
              </div>
              <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                <span className="fw-bold text-dark text-truncate" style={{ fontSize: '0.92rem' }}>
                  {row?.ownerName || `Sumber #${row?.resourceId}`}
                </span>
              </div>
            </div>

            <span
              className="badge rounded-pill font-semibold flex-shrink-0 ms-2"
              style={{
                backgroundColor: '#EFF8FF',
                color: '#175CD3',
                padding: '5px 10px',
                fontSize: '0.72rem',
              }}
            >
              {row?.role || 'VIEWER'}
            </span>
          </div>

          <div className="p-3 d-flex justify-content-between align-items-center">
            <span className="text-muted" style={{ fontSize: '0.78rem' }}>
              Tanggal Dibagikan
            </span>
            <span className="text-dark fw-medium" style={{ fontSize: '0.8rem' }}>
              {row?.createdAt
                ? moment(row.createdAt).locale('id').format('DD MMM YYYY, HH:mm')
                : '-'}
            </span>
          </div>

          <div
            className="p-2 px-3 border-top d-flex justify-content-end align-items-center"
            style={{ backgroundColor: '#F9FAFB' }}
          >
            <CustomButton
              color="primary"
              outline
              size="sm"
              onClick={() => handleSelectSource(row)}
              className="d-inline-flex align-items-center gap-1 text-nowrap rounded-pill px-3 py-1 font-semibold"
              style={{ fontSize: '0.825rem' }}
              testId={`select-shared-group-mobile-${row?.resourceId}`}
            >
              <span>Lihat Anggota</span>
              <ChevronRight size={15} />
            </CustomButton>
          </div>
        </CardBody>
      </Card>
    )
  }

  const customStyles = {
    headCells: {
      style: {
        color: '#667085',
        backgroundColor: '#F9FAFB',
      },
    },
    cells: {
      style: {},
    },
    rows: {
      style: {},
    },
  }

  if (resourceIdFromUrl && selectedSharedGroup) {
    return (
      <StaffList
        key={selectedSharedGroup.resourceId}
        sharedResourceId={selectedSharedGroup.resourceId}
        sharedOwnerName={selectedSharedGroup.ownerName}
        onBackToSharedGroups={handleBackToSources}
      />
    )
  }

  return (
    <div className="w-100 px-3">
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs
          breadCrumbActive={
            resourceIdFromUrl
              ? selectedSharedGroup?.ownerName || 'Anggota Bersama'
              : 'Daftar Anggota Bersama'
          }
          breadCrumbParent={
            resourceIdFromUrl ? 'Daftar Anggota Bersama' : 'Daftar Anggota'
          }
          breadCrumbParentLink={
            resourceIdFromUrl ? '/farmers/shared' : '/farmers'
          }
        />
      </div>

      <Card className="border-0 shadow-sm">
        <CardBody>
          {!resourceIdFromUrl ? (
            /* ============================================================ */
            /* VIEW 1: DAFTAR SUMBER (TAMPILAN AWAL)                         */
            /* ============================================================ */
            <DataTable
              customStyles={customStyles}
              columns={sourceColumns}
              data={sharedGroups}
              progressPending={isLoadingSharedGroups || isFetchingSharedGroups}
              responsive
              persistTableHead
              pointerOnHover
              highlightOnHover
              onRowClicked={(row) => handleSelectSource(row)}
              renderMobileCard={renderSourceMobileCard}
              progressComponent={<Spinner color="primary" />}
              sortIcon={<ChevronDown />}
              className="border p-0 border-1 rounded-top"
              noDataComponent={
                <div className="text-center py-5 text-muted">
                  <Users size={36} className="mb-2 text-secondary opacity-50" />
                  <p className="fw-semibold mb-0">
                    {isLoadingSharedGroups
                      ? 'Memuat data sumber grup...'
                      : 'Belum ada sumber grup yang dibagikan kepada Anda.'}
                  </p>
                </div>
              }
              pagination
              paginationServer
              paginationComponent={(props) => (
                <PMBPagination
                  rowCount={totalSharedGroups}
                  currentPage={sourcesPage}
                  rowsPerPage={sourcesSize}
                  setCurrentPage={(e) => setSourcesPage(e)}
                  setRowsPerPage={(e) => {
                    setSourcesSize(e)
                    setSourcesPage(0)
                  }}
                  props={props}
                  testId="shared-sources-pagination"
                />
              )}
              subHeader={true}
              subHeaderComponent={
                <div className="w-100">
                  <TabManageStaffGroup />
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div>
                      <h5 className="fw-semibold mb-1">Daftar Sumber Anggota Bersama</h5>
                      <p className="text-muted small mb-0">
                        Pilih sumber grup untuk melihat daftar anggota yang dibagikan kepada Anda.
                      </p>
                    </div>

                    <div style={{ minWidth: '260px' }}>
                      <FormGroup className="d-flex border rounded mb-0 bg-white">
                        <Input
                          placeholder="Cari sumber grup / pemilik..."
                          onChange={handleSourceSearchChange}
                          style={{ border: 'none', boxShadow: 'none' }}
                          value={sourcesSearch}
                          data-testid="shared-sources-search-input"
                        />
                        <InputGroupText
                          style={{
                            backgroundColor: '#ffffff',
                            border: 'none',
                          }}
                        >
                          <Search size={15} style={{ cursor: 'pointer' }} />
                        </InputGroupText>
                      </FormGroup>
                    </div>
                  </div>
                </div>
              }
            />
          ) : !selectedSharedGroup && !isLoadingSharedGroups ? (
            /* ============================================================ */
            /* VIEW 2A: SUMBER TIDAK DITEMUKAN / ERROR                      */
            /* ============================================================ */
            <div className="text-center py-5">
              <AlertCircle size={44} className="text-warning mb-2" />
              <h5 className="fw-bold">Sumber Grup Tidak Ditemukan</h5>
              <p className="text-muted mb-3">
                Sumber grup dengan ID #{resourceIdFromUrl} tidak ditemukan atau Anda tidak memiliki izin akses.
              </p>
              <CustomButton color="primary" onClick={handleBackToSources}>
                <ArrowLeft size={16} className="me-1" /> Kembali ke Grup yang Dibagikan
              </CustomButton>
            </div>
          ) : null}
      </CardBody>
    </Card>
  </div>
)
}

export default SharedFarmers
